import { QueryClient } from '@tanstack/react-query';
import {
	type AsyncStorage,
	type PersistedQuery,
	experimental_createQueryPersister,
} from '@tanstack/react-query-persist-client';
import { get, set, del, entries, createStore, type UseStore } from 'idb-keyval';
import config from '@/config';

// `cf-` prefix, same convention as `cf-observability` (the observability
// store) and the service worker's `cf-issue-index`/`cf-download-retry-queue`
// -- this is TanStack Query's transparent, last-seen-server-data cache
// (CLAUDE.md's "two caches, different ownership" distinction), disposable
// at any time with no user-visible consequence beyond a refetch.
// `canvasflow_db` (the pre-`cf-`-convention name) is reserved for the
// future explicit offline-issue-storage feature (topic 20's download
// manager) -- durable, user-initiated data, a different lifecycle
// entirely, and deserves a name that implies permanence rather than this
// convention's "disposable" connotation.
const store = createStore('cf-cache', 'react-query-cache');
const storage = idbStorage(store);

// One-time rename cleanup for returning users. Two legacy names to clear,
// from two successive renames of this same store: `canvasflow_db` (the
// original, shipped name) and `canvasflow_cache` (a short-lived
// intermediate rename, superseded by the `cf-` prefix convention above
// before it ever shipped -- cleaned up anyway in case anyone tested a
// build with it). Deleting either is safe unconditionally -- same
// disposable cache `LEGACY_QUERY_CACHE_KEY` below already treats as
// throwaway, just removed as a whole database instead of one key within
// it, since the database itself was renamed rather than just its
// internal shape changing.
function deleteLegacyQueryCacheDatabases(): void {
	indexedDB.deleteDatabase('canvasflow_db');
	indexedDB.deleteDatabase('canvasflow_cache');
}

export async function clearPersistStorage() {
	if (!storage.entries) {
		return;
	}

	for (const [key] of await storage.entries()) {
		await storage.removeItem(key);
	}
}

// The key the previous persister (createAsyncStoragePersister) used to
// store the *entire* dehydrated query cache as one JSON blob under a
// single IndexedDB entry. Superseded by queryPersister below, which
// stores one entry per query keyed by its own query hash instead --
// removed on boot (see bootQueryPersistence) so it doesn't linger as
// dead weight for returning users who last visited before this change.
const LEGACY_QUERY_CACHE_KEY = 'cf-query';

// In-memory retention (gcTime) and persisted-storage retention (maxAge)
// both follow the same online/offline split, per explicit product decision:
// this app is mostly "read a magazine issue" -- if the user leaves an issue
// and comes back within a couple hours while online, keep it around (both
// in memory and in IndexedDB); past that, they're unlikely to return to
// that exact issue, so let it go rather than hold it for no benefit (a
// fresh fetch on next visit is cheap while online). Offline, there's no
// "next fetch" to fall back on, so keep data resident for a full day
// instead of evicting it out from under a reader who's still using the
// app, just not currently looking at that screen.
export const CACHE_DURATION_ONLINE = 1000 * 60 * 60 * 2; // 2 hours
export const CACHE_DURATION_OFFLINE = 1000 * 60 * 60 * 24; // 24 hours

// Default freshness window while online, used by getQueryCacheOptions below.
// staleTime while offline is deliberately Infinity, not this -- offline,
// there's nothing to refetch from anyway (see getQueryCacheOptions), so
// treating cached data as eternally fresh just avoids a pointless fetch
// attempt on every mount; it doesn't mean offline data is kept any longer
// than CACHE_DURATION_OFFLINE (that's gcTime/maxAge's job, above).
export const STALE_TIME_ONLINE = 1000 * 60 * 60; // 1 hour

/**
 * The gcTime/staleTime/refetchInterval every entity query hook in this app
 * (Issue, Issues, Article, Preview, Collection, per-template Home/Section/
 * Games, Assets, Fonts, ...) should use, centralized so the online/offline
 * policy above is enforced consistently instead of ~22 files each
 * hand-rolling their own `cacheTime` value and `isOnline ? cacheTime :
 * Infinity` pattern (with the *values* drifting between 1 and 60 minutes
 * per file, and every one of them using Infinity -- not 24h -- for offline
 * gcTime, silently overriding defaultQueryClient's connectivity-aware
 * defaults above since per-query options always win over client defaults).
 *
 * ttlMinutes: publisher-configurable per-entity override (the pre-existing
 * `config.app.cache.ttl.<entity>` values -- getIssue, getAllIssues,
 * getFeatureArticles, styles, previewIssue) -- callers that have one pass
 * it through; it replaces CACHE_DURATION_ONLINE/STALE_TIME_ONLINE for that
 * entity's *online* gcTime/staleTime/refetchInterval specifically. Offline
 * behavior (staleTime: Infinity, gcTime: 24h) is intentionally NOT
 * overridable here -- that split is about connectivity, not per-entity
 * freshness, so it stays uniform regardless of which entity is asking.
 *
 * isAdmin bypasses caching entirely (staleTime/gcTime: 0) -- preserved from
 * the pre-existing per-hook pattern, unrelated to the online/offline split;
 * admins are expected to always see live, uncached data.
 */
export function getQueryCacheOptions(args: {
	/**
	 * Optional: route `loader`s omit it, having no trustworthy answer to
	 * give (see `CacheArgs.isOnline` in `shared/api/queries.ts`). Absent, it
	 * takes the online branch below -- the same values a loader would have
	 * produced by reading `navigator.onLine`, which on a cold boot is what
	 * it reported anyway. Nothing durable rides on this: the *persisted*
	 * cache's own retention is deliberately connectivity-independent (see
	 * `queryPersister`), so this only sets in-memory retention and
	 * freshness, which reset on the next load.
	 */
	isOnline?: boolean;
	isAdmin?: boolean;
	ttlMinutes?: number;
}): {
	staleTime: number;
	gcTime: number;
	refetchInterval: number | false;
} {
	const { isOnline = true, isAdmin = false, ttlMinutes } = args;
	if (isAdmin) {
		return { staleTime: 0, gcTime: 0, refetchInterval: false };
	}
	const onlineDuration =
		ttlMinutes !== undefined
			? ttlMinutes * 1000 * 60
			: CACHE_DURATION_ONLINE;
	const onlineStaleTime =
		ttlMinutes !== undefined ? ttlMinutes * 1000 * 60 : STALE_TIME_ONLINE;
	return {
		staleTime: isOnline ? onlineStaleTime : Infinity,
		gcTime: isOnline ? onlineDuration : CACHE_DURATION_OFFLINE,
		refetchInterval: isOnline ? onlineStaleTime : false,
	};
}

// TanStack Query's own fine-grained persister: one IndexedDB entry per
// query (key = `${QUERY_CACHE_PREFIX}-${queryHash}`), instead of the old
// approach of serializing the whole cache into a single field under
// LEGACY_QUERY_CACHE_KEY. Set as the default `persister` so every query
// benefits without per-hook changes: on first run (no in-memory data yet)
// it transparently restores that query's own entry from storage instead
// of hitting the network, and persists the result after every successful
// fetch.
const QUERY_CACHE_PREFIX = 'canvasflow-query-cache';

function createQueryPersister(maxAge: number) {
	return experimental_createQueryPersister({
		storage,
		prefix: QUERY_CACHE_PREFIX,
		maxAge,
		// Ties persisted-cache invalidation to the build version instead of
		// nuking localStorage wholesale on every deploy (see useBoot() in
		// App.tsx, which still does that for auth/session state -- a
		// separate, broader concern this doesn't touch). A stored entry
		// whose buster doesn't match the running version is treated as a
		// miss and refetched, so a deploy busts the query cache specifically
		// without logging the user out or discarding unrelated localStorage
		// state.
		buster: config.build.version,
		// idb-keyval stores structured-cloneable values directly -- no need
		// to (de)serialize to/from a string the way localStorage would
		// require.
		serialize: (persistedQuery: PersistedQuery) => persistedQuery,
		deserialize: (cached: PersistedQuery) => cached,
	});
}

// Always built with the generous (offline) maxAge, deliberately NOT
// connectivity-aware -- unlike in-memory gcTime below, this governs whether a
// *persisted* (IndexedDB) entry is still considered usable at all. Reusing
// CACHE_DURATION_ONLINE (2h) here was a real bug: `useIsOnline`'s reported
// status is only ever confirmed asynchronously (it seeds from the browser's
// unreliable `navigator.onLine` and corrects itself once `isReachable`
// resolves -- see that hook's own doc comment), but the home route's loader
// fires synchronously at route match, before that correction has had a
// chance to land. A reader who closes the app for a few hours and reopens it
// offline hit exactly that race: `navigator.onLine` still read `true` for
// that first instant, so this persister briefly ran with the 2h online
// maxAge, `isExpiredOrBusted` saw a several-hours-old (but otherwise
// perfectly good) entry as expired, deleted it, and the subsequent real
// fetch then failed outright offline -- reported as poets' magazines screen
// never loading from cache while offline, even though the identical
// mechanism elsewhere (morningstar's own section queries) happened not to
// hit the race in that user's testing. The persisted store's whole purpose
// is surviving exactly this "away from the network for a while" case, so it
// must never be pruned by a signal that can be transiently wrong -- pruning
// stale entries sooner while online is a nice-to-have, not worth that risk.
export const queryPersister = createQueryPersister(CACHE_DURATION_OFFLINE);

/**
 * A persisted entry carrying no actual entity -- `{ issue: null }`,
 * `{ article: null }` -- rather than real content.
 *
 * Builds before 2026-09-02 could write these: `fetchIssue`/`fetchArticle`
 * (shared/api/queries.ts) *resolved* such a placeholder when offline with
 * nothing in the in-memory cache, which the persister then stored as if it
 * were a successful fetch, on top of the reader's real cached copy. Those
 * two now throw instead, so nothing new is written -- but a device that ran
 * an affected build still has the poisoned entries, and they would keep
 * shadowing the real content until they aged out (24h) or a version bump
 * changed the persister's `buster`. Reported as an issue read online
 * minutes earlier coming back as "not downloaded for offline reading", on
 * every retry (real iOS device, airplane mode).
 *
 * Treated as a miss and cleaned up on boot so those devices self-heal on
 * the next launch rather than needing a release to be cut for it. Narrow on
 * purpose: only a single-key object whose one value is `null`, which is the
 * exact shape those two placeholders had. A real GraphQL result for these
 * queries always carries the entity alongside nothing else, so this cannot
 * match genuine content.
 */
function isNullEntityPlaceholder(data: unknown): boolean {
	if (!data || typeof data !== 'object' || Array.isArray(data)) {
		return false;
	}
	const values = Object.values(data as Record<string, unknown>);
	return values.length === 1 && values[0] === null;
}

/**
 * One-time boot step. Drops the old single-blob cache entry left over from
 * the previous persister if a returning user still has one, seeds the
 * in-memory query cache from the new per-query IndexedDB entries so
 * persisted data is available immediately (not just lazily on each query's
 * first mount), discards any poisoned null-entity placeholders a previously
 * shipped build may have written (see `isNullEntityPlaceholder`), and
 * opportunistically clears any per-query entries that have expired.
 */
export async function bootQueryPersistence(
	queryClient: QueryClient,
): Promise<void> {
	deleteLegacyQueryCacheDatabases();
	await storage.removeItem(LEGACY_QUERY_CACHE_KEY);
	await queryPersister.restoreQueries(queryClient);

	for (const query of queryClient.getQueryCache().getAll()) {
		if (!isNullEntityPlaceholder(query.state.data)) {
			continue;
		}
		// Dropped from both halves of the cache together: removing it only
		// in memory would leave the stored copy to be restored again on the
		// next launch, and removing it only from storage would leave this
		// session still reading the placeholder.
		queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
		await queryPersister.removeQueries({
			queryKey: query.queryKey,
			exact: true,
		});
	}

	await queryPersister.persisterGc();
}

/**
 * Applies the online/offline duration split above to defaultQueryClient's
 * in-memory gcTime only -- NOT the persister's maxAge (IndexedDB), which is
 * always the generous offline duration regardless of connectivity; see
 * `queryPersister`'s own comment for why coupling the two was a real bug.
 * In-memory retention carries none of that risk (it just resets on reload),
 * so it stays connectivity-aware: no benefit to holding a query resident in
 * memory for 24h while online and actively browsing. TanStack Query has no
 * networkMode-equivalent for this (no built-in way to make gcTime
 * connectivity-aware), so this needs an explicit call -- wired to the app's
 * connectivity signal (e.g. via useIsOnline()) once, at the App root. Doesn't
 * touch React state or force a re-render; queries just pick up the new
 * default the next time they render or are (re)observed.
 */
export function setCacheDurationsForConnectivity(isOnline: boolean): void {
	const gcTime = isOnline ? CACHE_DURATION_ONLINE : CACHE_DURATION_OFFLINE;
	defaultQueryClient.setDefaultOptions({
		queries: {
			...defaultQueryClient.getDefaultOptions().queries,
			gcTime,
		},
	});
}

export const defaultQueryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Safe initial default before the first
			// setCacheDurationsForConnectivity() call (App.tsx calls it as
			// soon as useIsOnline() resolves, which is immediate -- it's
			// seeded from navigator.onLine, never null -- but this is what's
			// in effect for the handful of queries that could theoretically
			// render before that first effect fires). Matches
			// `queryPersister`'s maxAge above (always the offline duration)
			// on purpose: if gcTime were shorter than maxAge, a query could
			// be evicted from the in-memory cache -- and therefore stop
			// being persisted going forward -- before the persisted copy's
			// own maxAge would have expired it, an inconsistency the
			// persister's docs call out explicitly. Was a flat 2 seconds
			// before any of this, which evicted almost immediately after
			// unmount and defeated persistence entirely.
			gcTime: CACHE_DURATION_OFFLINE,
			// Default baseline for any query that doesn't set its own
			// per-entity staleTime (config.app.cache.ttl-driven, most view
			// hooks already do). 1 hour, not seconds: this only controls when
			// an *already-rendered* query is eligible for a background
			// refetch on remount/refocus -- it doesn't block a publisher fix
			// from ever arriving, and doesn't affect first-load or explicit
			// invalidation. Was 10s.
			staleTime: 1000 * 60 * 60,
			// Static on purpose -- previously flipped between 'online' and
			// 'offlineFirst' by an effect in App.tsx keyed off useIsOnline(),
			// which just re-set the same client reference on every connectivity
			// change (see that effect's removal for why). 'offlineFirst' is the
			// right default for an installed offline reader per TanStack
			// Query's own networkMode docs: unlike 'online' (which pauses a
			// query outright while offline, never even attempting it),
			// 'offlineFirst' still attempts the fetch once and falls back to
			// whatever's cached/persisted on failure -- appropriate here since
			// the fetcher (GraphQL POST) has no service-worker-level fallback
			// of its own (topic 2's routes only cover images/fonts/audio/
			// video/styles, not the GraphQL endpoint) to make 'always' safe.
			// Individual hooks that need offline to be an instant, guaranteed
			// no-fetch-attempt (staleTime: isOnline ? cacheTime : Infinity)
			// already layer that on top per-query; this is just the baseline
			// for anything that doesn't.
			networkMode: 'offlineFirst',
			persister: queryPersister.persisterFn,
		},
	},
});

function idbStorage(idbStore: UseStore): AsyncStorage<PersistedQuery> {
	return {
		getItem: async (key) => get(key, idbStore),
		setItem: async (key, value) => set(key, value, idbStore),
		removeItem: async (key) => del(key, idbStore),
		entries: async () => entries(idbStore),
	};
}
